import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "@/lib/api";
import { mascotBase64 } from "@/assets/mascotBase64";
import { MapPin, ChevronRight, Sparkles, Coffee, Compass, MessageSquare, ShieldAlert, Plus, Tags, Users, BarChart3, Settings } from "lucide-react";
import { createProperty } from "@/lib/api";
import { toast } from "sonner";
import GuestLayout from "@/components/GuestLayout";
import { getPropertySlug } from "@/lib/store";

// Default images used when DB has no custom image uploaded for a property
const DEFAULT_CARD_IMAGES = {
  "varanasi": "https://images.unsplash.com/photo-1561361513-2d000a50f0dc?crop=entropy&cs=srgb&fm=jpg&q=80&w=800",
  "darjeeling-hostel": "https://images.unsplash.com/photo-1544634076-a90160ddf44c?crop=entropy&cs=srgb&fm=jpg&q=80&w=800",
  "darjeeling-home": "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?crop=entropy&cs=srgb&fm=jpg&q=80&w=800",
  "coming-soon": "https://images.unsplash.com/photo-1566073771259-6a8506099945?crop=entropy&cs=srgb&fm=jpg&q=80&w=800"
};

// Known garbage Unsplash placeholders that crept into the DB and should be treated as "no image"
const BLOCKED_URLS = ["photo-1469854523086"];

const getCardImage = (propertyId, dbImage) => {
  // If DB has a real image that isn't a known garbage placeholder, use it
  if (dbImage && dbImage.trim() && !BLOCKED_URLS.some(b => dbImage.includes(b))) {
    return dbImage;
  }
  return DEFAULT_CARD_IMAGES[propertyId] || DEFAULT_CARD_IMAGES["varanasi"];
};

// Static fallback so page renders in 0ms before API finishes
const DEFAULT_PROPERTIES = [
  { property_id: "varanasi", property_name: "Hidden Monkey Hostel, Varanasi", property_slug: "VaranasiHostel", property_location: "Chet Singh Ghat, Varanasi", header_image: "" },
  { property_id: "darjeeling-hostel", property_name: "Hidden Monkey Hostel, Darjeeling", property_slug: "DarjeelingHostel", property_location: "Near Railway Station, Darjeeling", header_image: "" },
  { property_id: "darjeeling-home", property_name: "Hidden Monkey Home, Darjeeling", property_slug: "DarjeelingHome", property_location: "Batasia Loop, Darjeeling", header_image: "" }
];

const COMING_SOON_PROPERTY = {
  property_id: "coming-soon",
  property_name: "Hidden Monkey Homes, Varanasi",
  property_location: "Varanasi (Coming Soon)",
  header_image: "",
  isComingSoon: true
};

const HIGHLIGHTS = [
  { icon: Coffee, label: "In-Room Dining", desc: "Order food & drinks straight to your room" },
  { icon: Compass, label: "Local Experiences", desc: "Curated walks, workshops & adventures" },
  { icon: MessageSquare, label: "Instant Requests", desc: "Towels, toiletries, anything you need" },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [globalBanner, setGlobalBanner] = useState("");
  
  // Admin Check
  const [isAdmin, setIsAdmin] = useState(false);
  const [showNewPropModal, setShowNewPropModal] = useState(false);
  const [newPropData, setNewPropData] = useState({ name: "", slug: "" });

  useEffect(() => {
    try {
      const userStr = localStorage.getItem("hm_user");
      if (userStr) {
        const user = JSON.parse(userStr);
        if (["admin", "global"].includes(user.role)) setIsAdmin(true);
      }
    } catch(e) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateProperty = async (e) => {
    e.preventDefault();
    if (!newPropData.name || !newPropData.slug) return toast.error("Please fill all fields");
    try {
      await createProperty({ property_name: newPropData.name, property_slug: newPropData.slug });
      toast.success("Property created in Draft mode!");
      setShowNewPropModal(false);
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create property");
    }
  };


  useEffect(() => {
    // If a property query param was passed (e.g. /?property=varanasi), redirect directly to the property portal
    try {
      const params = new URLSearchParams(window.location.search);
      const propParam = params.get("property");
      if (propParam) {
        const slug = getPropertySlug(propParam);
        navigate(`/${slug || propParam}`, { replace: true });
        return;
      }
    } catch (e) {}

    let isMounted = true;
    (async () => {
      try {
        // Try stale-while-revalidate for offline robustness
        const cached = localStorage.getItem("hm_cached_properties");
        if (cached && isMounted) {
          try {
            const parsed = JSON.parse(cached);
            if (Array.isArray(parsed) && parsed.length > 0) {
              setProperties(parsed);
              setLoading(false);
            }
          } catch(e) {}
        }
        
        const [propRes, settingsRes] = await Promise.allSettled([
          api.get("/properties"),
          api.get("/settings/global")
        ]);
        
        if (isMounted && settingsRes.status === "fulfilled" && settingsRes.value.data?.global_landing_banner) {
          setGlobalBanner(settingsRes.value.data.global_landing_banner);
        }
        
        if (isMounted && propRes.status === "fulfilled") {
          const res = propRes.value;
          if (Array.isArray(res.data) && res.data.length > 0) {
            setProperties(res.data);
            setLoading(false);
            try {
              localStorage.setItem("hm_cached_properties", JSON.stringify(res.data));
              res.data.forEach((prop) => {
                if (prop.property_id) {
                  localStorage.setItem(`hm_settings_${prop.property_id}`, JSON.stringify(prop));
                }
              });
            } catch (e) {}
          }
        }
      } catch (error) {
        console.error("Failed to refresh properties", error);
        if (isMounted) setLoading(false);
      }
    })();
    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allCards = [...properties, COMING_SOON_PROPERTY];

  return (
    <GuestLayout showNav={false}>
      <Helmet>
        <title>Hidden Monkey Stays | Powered by Koko</title>
        <meta name="description" content="Meet Koko, your digital stay companion. Select the property where you are staying to get your In-House Guide." />
      </Helmet>
      <main className="min-h-screen flex flex-col relative" data-testid="landing-page">
        {/* Hero Section */}
        <div className="relative h-[36vh] min-h-[280px] md:h-[45vh] lg:h-[55vh] md:min-h-[360px] overflow-hidden flex-shrink-0">
          <img
            src="https://images.unsplash.com/photo-1683914791867-7d65ac8893de?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
            alt="Hidden Monkey Stays"
            className="absolute inset-0 w-full h-full object-cover" fetchPriority="high" loading="eager"
            onError={(e) => { e.target.onerror = null; e.target.src = "https://images.unsplash.com/photo-1555854877-bab0e564b8d5?auto=format&fit=crop&q=80&w=1200"; }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#2A9D8F]/40 via-[#2A9D8F]/60 to-[#264653]/95" />
          
          <div className="absolute inset-0 flex flex-col justify-end p-6 pb-14 md:p-12 md:pb-28 lg:pb-36">
            <div className="absolute top-4 right-4 md:top-8 md:right-10 z-50">
              <button 
                onClick={() => {
                  if (isAdmin) {
                    navigate("/admin/dashboard");
                  } else {
                    navigate("/staff/login");
                  }
                }}
                className="text-sm md:text-base font-bold px-5 py-2.5 md:px-8 md:py-3 rounded-full border-2 border-white/40 text-white hover:bg-white/20 transition-all shadow-md backdrop-blur-md"
              >
                {isAdmin ? "Admin Portal" : "Login"}
              </button>
            </div>
            <div 
              className="absolute top-4 left-4 md:top-8 md:left-10 cursor-pointer z-50"
              onClick={() => navigate("/staff/login")}
              title="Staff Login"
            >
              <img src={mascotBase64} alt="Hidden Monkey" className="w-28 h-28 md:w-48 md:h-48 object-contain mascot-float drop-shadow-lg hover:scale-105 transition-transform" />
            </div>
            <p className="font-accent text-[#FFC107] text-lg md:text-2xl lg:text-3xl mb-1 animate-fade-in flex items-center gap-1.5">
              <Sparkles className="w-4 h-4 text-[#FFC107]" /> Welcome to
            </p>
            <h1 className="font-accent text-3xl md:text-5xl lg:text-6xl text-white mb-2 animate-fade-in-up font-bold tracking-tight">
              Hidden Monkey Stays
            </h1>
            <p className="text-white/80 text-sm md:text-base lg:text-lg font-medium max-w-xl">
              Meet <span className="text-[#FFC107] font-semibold">Koko</span>, your digital stay companion. Select your property to explore food, tours, and services.
            </p>
          </div>
        </div>

        {/* Property Cards — flex-1 so this section fills remaining screen height on desktop */}
        <section className="flex-1 bg-[#F9F7F2] pb-12">
          <div className="max-w-screen-xl mx-auto px-4 md:px-8 lg:px-12 -mt-6 md:-mt-16 lg:-mt-20 relative z-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5 lg:gap-6">
              {loading && properties.length === 0 ? (
                // Skeletons
                Array.from({ length: 4 }).map((_, i) => (
                  <div key={`skeleton-${i}`} className="guest-card bg-[#F9F7F2] animate-pulse rounded-2xl flex flex-col h-full border border-[#E8E4D9]/60 overflow-hidden">
                    <div className="w-full bg-[#E8E4D9]" style={{ aspectRatio: '4/3' }} />
                    <div className="p-3 md:p-5 flex-1 bg-white">
                      <div className="h-4 bg-[#E8E4D9] rounded w-3/4 mb-2"></div>
                      <div className="h-3 bg-[#E8E4D9] rounded w-1/2"></div>
                    </div>
                  </div>
                ))
              ) : (
                allCards.map((prop, index) => {
                  const cardImage = getCardImage(prop.property_id, prop.header_image);
                return (
                  <button
                    key={prop.property_id}
                    onClick={() => !prop.isComingSoon && navigate(`/${prop.property_slug || prop.property_id}`)}
                    className={`guest-card group text-left overflow-hidden rounded-2xl bg-white shadow-md hover:shadow-xl transition-all duration-300 border border-[#E8E4D9]/60 flex flex-col ${
                      prop.isComingSoon ? 'opacity-85 cursor-default' : 'cursor-pointer hover:-translate-y-1'
                    }`}
                  >
                    <div className="relative overflow-hidden w-full" style={{ aspectRatio: '4/3' }}>
                      <img
                        src={cardImage}
                        alt={prop.property_name}
                        className="w-full h-full object-cover img-zoom transition-transform duration-500 group-hover:scale-105"
                        loading={index < 2 ? "eager" : "lazy"}
                        fetchPriority={index < 2 ? "high" : "auto"}
                        onError={(e) => { e.target.onerror = null; e.target.src = DEFAULT_CARD_IMAGES[prop.property_id] || DEFAULT_CARD_IMAGES["varanasi"]; }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#264653]/80 via-transparent to-transparent opacity-90" />
                      {prop.isComingSoon ? (
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center backdrop-blur-[2px]">
                          <span className="bg-[#FFC107] text-[#264653] font-bold px-3 py-1 rounded-full uppercase tracking-wider text-xs shadow-md">Coming Soon</span>
                        </div>
                      ) : (
                        <div className="absolute bottom-2 right-2 md:bottom-3 md:right-3 w-7 h-7 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity">
                          <ChevronRight className="w-4 h-4" />
                        </div>
                      )}
                    </div>
                    <div className="p-3 md:p-5 bg-white flex-1 flex flex-col justify-between">
                      <div>
                        <h2 className="font-accent text-sm md:text-lg text-[#264653] font-bold leading-snug line-clamp-2">
                          {prop.property_name}
                        </h2>
                        <p className="text-xs md:text-sm text-[#636E72] flex items-center gap-1 mt-1.5 line-clamp-1">
                          <MapPin className="w-3 h-3 text-[#2A9D8F] flex-shrink-0" /> {prop.property_location}
                        </p>
                      </div>
                      {!prop.isComingSoon && (
                        <span className="text-[11px] md:text-sm font-bold text-[#2A9D8F] mt-3 md:mt-4 inline-flex items-center gap-0.5 group-hover:text-[#218377]">
                          Open In-House Guide <ChevronRight className="w-3 h-3" />
                        </span>
                      )}
                    </div>
                  </button>
                );
              })
              )}
            </div>
          </div>

          {/* What You Get — fills the white void on desktop */}
          <div className="max-w-screen-xl mx-auto px-4 md:px-8 lg:px-12 mt-10 md:mt-16">
            <h2 className="font-accent text-center text-lg md:text-2xl font-bold text-[#264653] mb-2">
              Everything at your fingertips with Koko
            </h2>
            <p className="text-center text-xs md:text-sm text-[#636E72] mb-6 md:mb-10 max-w-md mx-auto">
              Your self-serve companion for in-room dining, local tours, and instant requests.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-6 lg:gap-8">
              {HIGHLIGHTS.map((h) => (
                <div key={h.label} className="bg-white rounded-xl p-4 md:p-8 text-center shadow-sm border border-[#E8E4D9]/60 hover:shadow-md transition-shadow">
                  <div className="w-10 h-10 md:w-14 md:h-14 rounded-full bg-[#2A9D8F]/10 flex items-center justify-center mx-auto mb-3 md:mb-4">
                    <h.icon className="w-5 h-5 md:w-7 md:h-7 text-[#2A9D8F]" />
                  </div>
                  <h3 className="text-xs md:text-base font-bold text-[#264653] mb-1 md:mb-2">{h.label}</h3>
                  <p className="text-[10px] md:text-sm text-[#636E72] leading-snug">{h.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
    </GuestLayout>
  );
}
